import {
  type AuthCredential,
  type User as FirebaseUser,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  linkWithCredential,
  OAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { auth, db, googleProvider, microsoftProvider } from '../config/firebase';
import { isEmailAllowed, isExistingUser } from '../services/authWhitelistService';
import { syncUserProfileToGroups } from '../services/groupService';
import { clearPrefetch, prefetchPersonalItems } from '../services/prefetch';
import type { AccountLinkRequired, AuthContextType, User } from '../types';

// ドメイン制限エラーのカスタムエラークラス
export class DomainRestrictionError extends Error {
  code: string;
  constructor(message: string) {
    super(message);
    this.name = 'DomainRestrictionError';
    this.code = 'auth/domain-not-allowed';
  }
}

/**
 * 同じメールアドレスのアカウントが別のログイン手段で既に存在するときに投げる。
 *
 * Firebase は「1メールアドレスにつき1アカウント」が既定のため、
 * 例えば Google で登録済みのアドレスに Microsoft でサインインすると
 * auth/account-exists-with-different-credential で弾かれる。
 * 既存アカウント（uid）を保ったままログイン手段を増やすには、
 * 既存の手段で本人確認してから linkWithCredential する必要がある。
 */
export class AccountLinkRequiredError extends Error implements AccountLinkRequired {
  code = 'auth/account-link-required' as const;
  /** メール列挙保護が有効だと Firebase から取得できないことがあるため null を許容する */
  email: string | null;
  pendingCredential: AuthCredential;
  existingProvider: 'google' | 'microsoft';

  constructor(
    email: string | null,
    pendingCredential: AuthCredential,
    attempted: 'google' | 'microsoft',
  ) {
    // 対応プロバイダは Google と Microsoft の2つだけなので、
    // 今回試した方でない側が既存のログイン手段になる。
    const existingProvider = attempted === 'google' ? 'microsoft' : 'google';
    const providerLabel = existingProvider === 'google' ? 'Google' : 'Microsoft';
    super(
      email
        ? `このメールアドレス（${email}）は既に${providerLabel}アカウントで登録されています。`
        : `このメールアドレスは既に${providerLabel}アカウントで登録されています。`,
    );
    this.name = 'AccountLinkRequiredError';
    this.email = email;
    this.pendingCredential = pendingCredential;
    this.existingProvider = existingProvider;
  }
}

/**
 * OAuth の ID トークン（JWT）からメールアドレスを取り出す。
 *
 * Firebase のメール列挙保護が有効だと、account-exists-with-different-credential の
 * error.customData.email が空で返ることがある。その場合でも、サインインに使った
 * 資格情報自体にはメールアドレスが入っているので、そこから復元する。
 */
const emailFromCredential = (credential: any): string | null => {
  const idToken = credential?.idToken;
  if (typeof idToken !== 'string') return null;
  try {
    const [, payload] = idToken.split('.');
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json);
    // Microsoft は email が無く preferred_username / upn に入ることがある
    const value = claims.email || claims.preferred_username || claims.upn;
    return typeof value === 'string' && value.includes('@') ? value : null;
  } catch {
    return null;
  }
};

/** 資格情報の取り出しとエラー変換。資格情報が取れない場合は元のエラーをそのまま投げる。 */
const toAccountLinkError = (error: any, attempted: 'google' | 'microsoft'): never => {
  const credential =
    attempted === 'google'
      ? GoogleAuthProvider.credentialFromError(error)
      : OAuthProvider.credentialFromError(error);
  if (!credential) {
    // ここに来ると連携画面を出せず「別の認証方法が登録されています」で終わってしまう。
    // 原因の切り分けができるよう、取得できた情報を残す。
    console.error('[link] 資格情報を取り出せませんでした', {
      attempted,
      code: error?.code,
      hasCustomData: Boolean(error?.customData),
      customDataEmail: error?.customData?.email ?? null,
    });
    throw error;
  }
  // メールアドレスは案内文と login_hint に使うだけなので、取れなくても連携は進められる
  const email = error?.customData?.email || emailFromCredential(credential);
  throw new AccountLinkRequiredError(email, credential, attempted);
};

/**
 * 本人確認用のプロバイダを作る。
 * 共有インスタンスに login_hint を設定すると以後のログインにも残るため、都度作る。
 */
const buildVerifyProvider = (provider: 'google' | 'microsoft', email: string | null) => {
  const params: Record<string, string> = {};
  if (email) {
    params.login_hint = email;
  }
  if (provider === 'google') {
    const p = new GoogleAuthProvider();
    p.setCustomParameters(params);
    return p;
  }
  const p = new OAuthProvider('microsoft.com');
  const tenant = import.meta.env.VITE_AZURE_TENANT_ID;
  if (tenant) {
    params.tenant = tenant;
  }
  p.setCustomParameters(params);
  return p;
};

// プロバイダーを判定するヘルパー関数
const detectProvider = (firebaseUser: FirebaseUser): 'google' | 'microsoft' => {
  const providerData = firebaseUser.providerData;
  if (providerData && providerData.length > 0) {
    const providerId = providerData[0].providerId;
    if (providerId === 'microsoft.com') {
      return 'microsoft';
    }
  }
  return 'google';
};

// ログイン可否を判定し、許可外なら即ログアウトして例外を投げる共通処理。
// - 既存登録ユーザー（Firestore に users/{uid} が存在）は後方互換のため常に許可する。
// - 新規ユーザーは許可ドメイン（または allowedEmails）のみ許可する。
// 許可外の新規ユーザーには users ドキュメントを一切作らせないため、
// convertFirebaseUser（ドキュメント作成を伴う）より前に必ず呼び出すこと。
const ensureUserAllowed = async (firebaseUser: FirebaseUser): Promise<void> => {
  const existingUser = await isExistingUser(firebaseUser.uid);
  if (existingUser) {
    return;
  }

  const provider = detectProvider(firebaseUser);
  const { allowed, reason } = await isEmailAllowed(firebaseUser.email, provider);
  if (!allowed) {
    // 許可外ドメインの新規ユーザーは即ログアウトし、ドキュメントを残さない
    await firebaseSignOut(auth);
    throw new DomainRestrictionError(reason || 'このドメインでのログインは許可されていません');
  }
};

// 認証コンテキスト作成
const AuthContext = createContext<AuthContextType | null>(null);

// 認証プロバイダーコンポーネント
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Firebase User を内部User型に変換
  const convertFirebaseUser = useCallback(async (firebaseUser: FirebaseUser): Promise<User> => {
    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    const userData = userDoc.data();
    const provider = detectProvider(firebaseUser);

    const user: User = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      provider,
      createdAt: userData?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Firestoreにユーザー情報を保存/更新
    await setDoc(
      doc(db, 'users', firebaseUser.uid),
      {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        provider: user.provider,
        createdAt: userData?.createdAt || user.createdAt,
        updatedAt: user.updatedAt,
      },
      { merge: true },
    );

    return user;
  }, []);

  // Google SSO ログイン
  const signInWithGoogle = async (): Promise<void> => {
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, googleProvider);

      // ログイン可否を判定（許可外の新規ユーザーはここで弾く）
      await ensureUserAllowed(result.user);

      const user = await convertFirebaseUser(result.user);
      setUser(user);
    } catch (error: any) {
      if (error?.code === 'auth/account-exists-with-different-credential') {
        // 既に Microsoft で登録済みのアドレス。連携を促す
        toAccountLinkError(error, 'google');
      }
      console.error('Google Sign-in Error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Microsoft Entra ID SSO ログイン
  const signInWithMicrosoft = async (): Promise<void> => {
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, microsoftProvider);

      // Microsoft は現状 isEmailAllowed が無条件で許可を返すため実質は素通りだが、
      // Google 側と処理をそろえておく。将来 Microsoft にも制限をかける場合
      // （Prism 対応など）、users ドキュメントを作る前に弾く必要があるため。
      await ensureUserAllowed(result.user);

      const user = await convertFirebaseUser(result.user);
      setUser(user);
    } catch (error: any) {
      if (error?.code === 'auth/account-exists-with-different-credential') {
        // 既に Google で登録済みのアドレス。連携を促す
        toAccountLinkError(error, 'microsoft');
      }
      console.error('Microsoft Sign-in Error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * 既存アカウントに、弾かれた側のログイン手段を追加する。
   *
   * 既存の手段でサインインして本人確認したうえで link するので、
   * uid は変わらず、これまでのアイテムや案件をそのまま引き継げる。
   * 2回目以降はどちらの手段でも直接ログインできる。
   */
  const linkPendingCredential = async (linkError: AccountLinkRequired): Promise<void> => {
    try {
      setLoading(true);
      const provider = buildVerifyProvider(linkError.existingProvider, linkError.email);
      const verified = await signInWithPopup(auth, provider);

      // 別アカウントでサインインされた場合に、無関係なアカウントへ紐づけない。
      // メールアドレスが取れなかった場合はこの照合を飛ばし、Firebase 側の
      // 検証（credential-already-in-use 等）に委ねる。
      if (linkError.email && verified.user.email?.toLowerCase() !== linkError.email.toLowerCase()) {
        await firebaseSignOut(auth);
        throw new Error(
          `${linkError.email} でログインしてください。別のアカウントが選択されました。`,
        );
      }

      await ensureUserAllowed(verified.user);
      await linkWithCredential(verified.user, linkError.pendingCredential as AuthCredential);

      const user = await convertFirebaseUser(verified.user);
      setUser(user);
    } catch (error) {
      console.error('Account Link Error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // ログアウト
  const signOut = async (): Promise<void> => {
    try {
      setLoading(true);
      await firebaseSignOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Sign-out Error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 認証状態監視
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // 許可外の新規ユーザーが users ドキュメントを作る前に弾く。
          // （これを欠くと、1回目のログイン試行でドキュメントが作られ、
          //   2回目に「既存ユーザー」と誤判定されてドメイン制限をバイパスできてしまう）
          await ensureUserAllowed(firebaseUser);

          // 画面が出るのを待たずにマイアイテムを取りに行かせる（直列だと
          // バックエンドのコールドスタートぶんがまるごと待ち時間になるため）
          prefetchPersonalItems(firebaseUser.uid);

          const user = await convertFirebaseUser(firebaseUser);
          setUser(user);

          // ログイン時に所属グループのメンバー情報を最新のプロフィールで同期
          if (user.displayName) {
            syncUserProfileToGroups(user.uid, {
              displayName: user.displayName,
              photoURL: user.photoURL || undefined,
            });
          }
        } else {
          clearPrefetch();
          setUser(null);
        }
      } catch (error) {
        console.error('Auth State Change Error:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [convertFirebaseUser]);

  const value: AuthContextType = {
    user,
    loading,
    signInWithGoogle,
    signInWithMicrosoft,
    linkPendingCredential,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// カスタムフック
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
